import { useRef } from 'react';
import type { PhotoTransform } from '@jobos/shared';
import { viewportFromTransform, offsetFromViewportTopLeft } from '@/lib/photoNavigator';

// Minimap for repositioning a zoomed slot photo. Shows a small replica of the
// slot (photo `contain`, slot aspect ratio) with a draggable white rectangle
// marking the part currently visible on the document. Dragging the rectangle
// pans the slot photo. Reposition only — zoom stays on the slider.
export interface PhotoNavigatorProps {
  photoUrl: string;
  transform: PhotoTransform;
  /** Slot width / height, so the navigator matches the slot's shape. */
  aspectRatio: number;
  onChange: (offsetX: number, offsetY: number) => void;
}

export function PhotoNavigator({ photoUrl, transform, aspectRatio, onChange }: PhotoNavigatorProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const view = viewportFromTransform(transform);

  const startDrag = (e: React.MouseEvent) => {
    const frame = frameRef.current;
    if (!frame) return;
    e.preventDefault();
    const rect = frame.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = view.left;
    const startTop = view.top;
    const maxTopLeft = Math.max(0, 1 - view.size);

    const clamp = (v: number) => Math.max(0, Math.min(maxTopLeft, v));
    const move = (ev: MouseEvent) => {
      const left = clamp(startLeft + (ev.clientX - startX) / rect.width);
      const top = clamp(startTop + (ev.clientY - startY) / rect.height);
      const { offsetX, offsetY } = offsetFromViewportTopLeft(left, top, transform.zoom);
      onChange(offsetX, offsetY);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div className="ins-nav" style={{ aspectRatio }} ref={frameRef} data-testid="photo-navigator">
      <img className="ins-nav-photo" src={photoUrl} alt="" draggable={false} />
      <div
        className="ins-nav-viewport"
        data-testid="photo-navigator-viewport"
        style={{
          left: `${view.left * 100}%`,
          top: `${view.top * 100}%`,
          width: `${view.size * 100}%`,
          height: `${view.size * 100}%`,
        }}
        onMouseDown={startDrag}
      />
    </div>
  );
}
